import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Check, X, Loader2, Trophy } from "lucide-react";
import { getCertifyQuestions, type CertifyQuestion } from "@/lib/certify.functions";
import { useUserXp, RANKS, XP_PER_CERTIFY } from "@/hooks/use-user-xp";
import { track } from "@/lib/analytics";
import { Confetti } from "@/components/Confetti";
import { FieldCertificate } from "@/components/discovery/FieldCertificate";
import { ContextualDrop } from "@/components/discovery/ContextualDrop";

/** Deterministic-free shuffle - quiz answer order is always randomised. */
function shuffle(q: CertifyQuestion): CertifyQuestion {
  const pairs = q.options.map((o, i) => ({ o, correct: i === q.answerIndex }));
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j]!, pairs[i]!];
  }
  return { ...q, options: pairs.map((p) => p.o), answerIndex: pairs.findIndex((p) => p.correct) };
}

function RankBar({ xp, gained = 0 }: { xp: number; gained?: number }) {
  // Start the bar at the pre-award value, then fill to the new value on mount.
  const [shown, setShown] = useState(xp - gained);
  useEffect(() => {
    const t = setTimeout(() => setShown(xp), 120);
    return () => clearTimeout(t);
  }, [xp]);

  const { current, next, progress, toNext } = (() => {
    let index = 0;
    for (let i = 0; i < RANKS.length; i++) if (shown >= RANKS[i]!.min) index = i;
    const cur = RANKS[index]!;
    const nxt = RANKS[index + 1] ?? null;
    const span = nxt ? nxt.min - cur.min : 1;
    return {
      current: cur,
      next: nxt,
      progress: nxt ? Math.min(1, (shown - cur.min) / span) : 1,
      toNext: nxt ? Math.max(0, nxt.min - shown) : 0,
    };
  })();

  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.25em]">
        <span className="text-primary">{current.name}</span>
        <span className="text-muted-foreground">{shown} XP</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-border/60">
        <div
          className="h-full rounded-full bg-primary transition-all duration-1000 ease-out"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {next ? <><span>{toNext}</span> <span>XP to</span> <span>{next.name}</span></> : "Top rank reached. Nobody out-verifies you."}
      </p>
    </div>
  );
}

/**
 * End-of-article verification lock. Two questions drawn from the article the
 * reader just finished - passing marks the discovery "certified" and pays XP.
 * Works with no account: progress is local until they choose to save it.
 */
export function CertifyDiscovery({
  slug,
  title,
  categorySlug,
  categoryLabel,
}: {
  slug: string;
  title: string;
  categorySlug?: string | null;
  categoryLabel?: string | null;
}) {
  const fetchQuestions = useServerFn(getCertifyQuestions);
  const xp = useUserXp(slug);
  const [questions, setQuestions] = useState<CertifyQuestion[] | null>(null);
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);
  const [gained, setGained] = useState(0);

  const load = useMutation({
    mutationFn: () => fetchQuestions({ data: { slug } }),
    onSuccess: (res) => {
      const qs = (res?.questions ?? []).map(shuffle);
      if (!qs.length) return;
      setQuestions(qs);
      track("Certify Start", { slug });
    },
  });

  const alreadyCertified = xp.hydrated && xp.certifiedHere !== undefined && !done;

  const answer = (i: number) => {
    if (picked !== null || !questions) return;
    setPicked(i);
    const q = questions[step]!;
    const isRight = i === q.answerIndex;
    if (isRight) setCorrectCount((c) => c + 1);
    try {
      navigator.vibrate?.(isRight ? [10, 30, 14] : 22);
    } catch {
      /* unsupported */
    }
  };

  const retry = () => {
    if (!questions) return;
    setQuestions(questions.map(shuffle));
    setStep(0);
    setPicked(null);
    setCorrectCount(0);
    setDone(false);
    track("Certify Retry", { slug });
  };

  const next = () => {
    if (!questions) return;
    if (step + 1 < questions.length) {
      setStep(step + 1);
      setPicked(null);
      return;
    }
    const total = questions.length;
    const firstTime = xp.certifiedHere === undefined && correctCount >= total;
    const won = xp.certify(correctCount, total);
    setGained(won || (firstTime ? XP_PER_CERTIFY : 0));
    setDone(true);
    track("Certify Complete", { slug, correct: correctCount, total });

  };

  return (
    <section className="relative my-10 overflow-hidden rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 md:p-7">
      {done && gained > 0 && <Confetti />}
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <ShieldCheck className="h-3.5 w-3.5" /> Certify this discovery
      </div>

      {/* Already certified on a previous read */}
      {alreadyCertified && (
        <>
          <p className="mt-3 font-display text-xl uppercase leading-tight md:text-2xl">
            Certified.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            You've already verified this one. {xp.certifiedCount} discoveries certified so far.
          </p>
          <RankBar xp={xp.xp} />
        </>
      )}

      {/* Intro */}
      {!alreadyCertified && !questions && !done && (
        <>
          <p className="mt-3 font-display text-xl uppercase leading-tight md:text-2xl">
            Two questions from this article.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Both right earns +50 XP. No account needed.
          </p>

          <button
            type="button"
            onClick={() => load.mutate()}
            disabled={load.isPending}
            className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-primary bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
          >
            {load.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {load.isPending ? "Loading questions…" : "Start the check"}
          </button>
          {load.isError && (
            <p className="mt-3 text-xs text-muted-foreground">
              Couldn't load the questions right now. Try again in a moment.
            </p>
          )}
          {xp.hydrated && xp.xp > 0 && <RankBar xp={xp.xp} />}
        </>
      )}

      {/* Questions */}
      {!alreadyCertified && questions && !done && (
        <div className="animate-fade-in">
          <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            Question {step + 1} of {questions.length}
          </p>
          <p className="mt-2 font-display text-lg uppercase leading-tight md:text-xl">
            {questions[step]!.question}
          </p>
          <div className="mt-4 grid gap-2">
            {questions[step]!.options.map((opt, i) => {
              const isAnswer = i === questions[step]!.answerIndex;
              const chosen = picked === i;
              const state =
                picked === null
                  ? "border-border bg-background hover:border-primary hover:bg-primary/10"
                  : isAnswer
                    ? "border-primary bg-primary/15 text-foreground"
                    : chosen
                      ? "border-destructive/60 bg-destructive/10"
                      : "border-border opacity-50";
              return (
                <button
                  key={`${opt}-${i}`}
                  type="button"
                  onClick={() => answer(i)}
                  disabled={picked !== null}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition active:scale-[0.99] ${state}`}
                >
                  <span>{opt}</span>
                  {picked !== null && isAnswer && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  {picked !== null && chosen && !isAnswer && <X className="h-4 w-4 shrink-0 text-destructive" />}
                </button>
              );
            })}
          </div>
          {picked !== null && (
            <div className="mt-4 animate-fade-in">
              {questions[step]!.explanation && (
                <p className="text-sm text-muted-foreground">{questions[step]!.explanation}</p>
              )}
              <button
                type="button"
                onClick={next}
                className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-primary bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.98]"
              >
                {step + 1 < questions.length ? "Next question" : "See my result"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {done && (
        <div className="animate-fade-in">
          {correctCount === (questions?.length ?? 2) ? (
            <>
              <p className="mt-3 flex items-center gap-2 font-display text-xl uppercase leading-tight md:text-2xl">
                <Trophy className="h-5 w-5 text-primary" />
                Certified. Flawless.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                “{title}” is now in your research archive. +{gained} XP.
              </p>
            </>
          ) : (
            <>
              <p className="mt-3 font-display text-xl uppercase leading-tight md:text-2xl">
                Not certified. Yet.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {correctCount}/{questions?.length ?? 2} correct. Both have to land - the answers are all in
                the story above.
              </p>
              <button
                type="button"
                onClick={retry}
                className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-primary bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.98]"
              >
                <ShieldCheck className="h-4 w-4" /> Try again
              </button>
            </>
          )}
          <RankBar xp={xp.xp} gained={gained} />

          {gained > 0 && (
            <>
              <FieldCertificate
                title={title}
                slug={slug}
                rank={xp.rank.current.name}
                xp={xp.xp}
                certifiedCount={xp.certifiedCount}
              />
              <ContextualDrop categorySlug={categorySlug} categoryLabel={categoryLabel} />
            </>
          )}



          {xp.certifiedCount >= 2 && !xp.savePromptDismissed && (
            <div className="mt-5 rounded-2xl border border-border bg-card/60 p-4">
              <p className="text-sm font-semibold">
                <span>{xp.xp}</span> <span>XP and</span> <span>{xp.certifiedCount}</span>{" "}
                <span>certifications live only in this browser.</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/auth"
                  className="rounded-2xl border border-primary bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground"
                >
                  Save my progress
                </Link>
                <button
                  type="button"
                  onClick={xp.dismissSavePrompt}
                  className="rounded-2xl border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-muted-foreground"
                >
                  Not now
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </section>
  );
}
